import React, { useEffect, useState } from "react";
import api, { formatErr } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { toast } from "sonner";
import { Sparkles } from "lucide-react";

export default function AdminBot() {
  const [unknown, setUnknown] = useState([]);
  const [intents, setIntents] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [botMode, setBotMode] = useState("live");
  const [form, setForm] = useState({ query: "", event_type: "", keywords: [], max_budget: null, reply: "" });
  const [suggestingId, setSuggestingId] = useState(null);

  const load = async () => {
    try {
      const [u, i, f, c] = await Promise.all([
        api.get("/admin/bot/unknown"), 
        api.get("/admin/bot/intents"),
        api.get("/admin/bot/feedback"),
        api.get("/config")
      ]);
      setUnknown(u.data); 
      setIntents(i.data);
      setFeedback(f.data);
      setBotMode(c.data.bot_mode || "live");
    } catch (e) {
      setUnknown([]); setIntents([]); setFeedback([]);
    }
  };
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/bot/intents", { ...form, max_budget: form.max_budget ? Number(form.max_budget) : null });
      toast.success("Intent saved");
      setForm({ query: "", event_type: "", keywords: [], max_budget: null, reply: "" });
      load();
    } catch (err) { toast.error(formatErr(err)); }
  };

  const remove = async (id) => {
    await api.delete(`/admin/bot/unknown/${id}`); load();
  };

  const removeFeedback = async (id) => {
    await api.delete(`/admin/bot/feedback/${id}`); load();
  };

  const toggleMode = async () => {
    const next = botMode === "live" ? "training" : "live";
    try {
      await api.post("/admin/config", { key: "bot_mode", value: next });
      setBotMode(next);
      toast.success(`Switched to ${next} mode`);
    } catch (e) { toast.error("Failed to switch mode"); }
  };

  const suggest = async (id) => {
    setSuggestingId(id);
    try {
      const { data } = await api.post("/admin/bot/suggest-fix", { id });
      setFeedback(prev => prev.map(f => f.id === id ? { ...f, ai_suggestion: data.suggestion } : f));
      toast.success("AI Suggestion generated!");
    } catch (e) { toast.error("Failed to generate suggestion"); }
    setSuggestingId(null);
  };

  return (
    <div data-testid="admin-bot">
      <div className="flex justify-between items-center mb-8">
        <h1 className="font-serif-display text-4xl">Bot Training</h1>
        <div className="flex items-center gap-3 bg-muted px-4 py-2 rounded-full">
          <span className="text-sm font-medium">Mode: <span className="capitalize text-primary">{botMode}</span></span>
          <Button size="sm" variant="outline" className="rounded-full h-8" onClick={toggleMode}>
            Switch to {botMode === "live" ? "Training" : "Live"}
          </Button>
        </div>
      </div>
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-background rounded-2xl border border-border p-6">
          <h2 className="font-serif-display text-2xl mb-4">Unknown Queries</h2>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {unknown.map((q) => (
              <div key={q.id} className="flex justify-between items-start gap-2 p-3 bg-muted rounded-lg" data-testid={`uq-${q.id}`}>
                <div className="text-sm flex-1">{q.query}<div className="text-xs text-muted-foreground">{new Date(q.created_at).toLocaleString()}</div></div>
                <button onClick={() => setForm({ ...form, query: q.query })} className="text-xs text-primary" data-testid={`map-${q.id}`}>Map →</button>
                <button onClick={() => remove(q.id)} className="text-xs text-muted-foreground">×</button>
              </div>
            ))}
            {unknown.length === 0 && <div className="text-sm text-muted-foreground p-3">No unknown queries 🎉</div>}
          </div>
        </div>
        <div className="bg-background rounded-2xl border border-border p-6">
          <h2 className="font-serif-display text-2xl mb-4">Add Intent Mapping</h2>
          <form onSubmit={save} className="space-y-3">
            <div><Label>Query Pattern</Label><Input required value={form.query} onChange={(e) => setForm({ ...form, query: e.target.value })} data-testid="intent-query" placeholder="e.g. gf surprise" /></div>
            <div><Label>Map to Event Type</Label>
              <select className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm" value={form.event_type} onChange={(e) => setForm({ ...form, event_type: e.target.value })} data-testid="intent-event">
                <option value="">-</option>
                {["birthday", "romantic", "anniversary", "baby-shower", "festival", "kids"].map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div><Label>Keywords (comma)</Label><Input value={form.keywords.join(",")} onChange={(e) => setForm({ ...form, keywords: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) })} /></div>
            <div><Label>Max Budget</Label><Input type="number" value={form.max_budget || ""} onChange={(e) => setForm({ ...form, max_budget: e.target.value })} /></div>
            <div><Label>Custom Reply</Label><Textarea value={form.reply} onChange={(e) => setForm({ ...form, reply: e.target.value })} /></div>
            <Button type="submit" className="w-full rounded-full bg-primary" data-testid="intent-save">Save Intent</Button>
          </form>
          <h3 className="font-serif-display text-xl mt-6 mb-3">Saved Intents ({intents.length})</h3>
          <div className="space-y-1 max-h-40 overflow-y-auto">
            {intents.map((i) => <div key={i.id} className="text-xs p-2 bg-muted rounded">{i.query} → {i.event_type}</div>)}
          </div>
        </div>
      </div>

      <div className="mt-8 bg-background rounded-2xl border border-border p-6">
        <h2 className="font-serif-display text-2xl mb-4">User Feedback & Corrections</h2>
        <div className="grid gap-4">
          {feedback.map((f) => (
            <div key={f.id} className="p-4 bg-muted/50 rounded-xl border border-border relative group">
              <button onClick={() => removeFeedback(f.id)} className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition text-muted-foreground hover:text-red-500">×</button>
              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold mb-1">User Query</div>
                  <div className="text-sm">"{f.query}"</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold mb-1">Bot Replied</div>
                  <div className="text-sm text-red-500/70 italic">"{f.bot_reply}"</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-primary font-bold mb-1">User Correction</div>
                  <div className="text-sm font-medium">"{f.user_feedback}"</div>
                </div>
              </div>
              
              {f.ai_suggestion && (
                <div className="mt-4 p-3 bg-primary/10 rounded-lg border border-primary/20 animate-in fade-in slide-in-from-top-2">
                  <div className="text-[10px] uppercase tracking-wider text-primary font-bold mb-1 flex items-center gap-1">
                    <Sparkles className="w-3 h-3" /> AI Suggested Fix
                  </div>
                  <div className="text-sm font-serif italic text-primary">"{f.ai_suggestion}"</div>
                  <div className="mt-2 flex gap-2">
                    <Button size="sm" className="h-7 text-[10px]" onClick={() => setForm({ ...form, query: f.query, reply: f.ai_suggestion })}>Use Suggestion</Button>
                    <Button size="sm" variant="ghost" className="h-7 text-[10px]" onClick={() => setFeedback(prev => prev.map(item => item.id === f.id ? { ...item, ai_suggestion: null } : item))}>Discard</Button>
                  </div>
                </div>
              )}
              
              {!f.ai_suggestion && (
                <div className="mt-4 flex gap-2">
                  <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => suggest(f.id)} disabled={suggestingId === f.id}>
                    {suggestingId === f.id ? "Generating..." : "Get AI Suggestion"}
                  </Button>
                  <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={() => setForm({ ...form, query: f.query, reply: f.user_feedback })}>Manual Fix</Button>
                </div>
              )}
            </div>
          ))}
          {feedback.length === 0 && <div className="text-center py-8 text-muted-foreground italic">No feedback received yet. Switch to Training Mode to capture corrections!</div>}
        </div>
      </div>
    </div>
  );
}
